/**
 * BoardingOverlay — the first interaction.
 *
 * Plan 05-01 Task 3 (NAV-02 entry surface). Rendered at z-index 150 above
 * LoadingHUD (100) so it stays above the loading chrome but below DebugHUD
 * (200). Visible if and only if `navState === 'idle'` — the machine's initial
 * state before any user action.
 *
 * Click "Board the streetcar" dispatches {type:'START'} via useNavSend. This
 * ALSO serves as the browser autoplay-gesture unlock that Plan 05-05 audio
 * sources depend on — the click is a user-initiated interaction in the
 * page's tab, so subsequent AudioContext playback is permitted.
 *
 * Chrome: AESTHETIC-BRIEF Rule 7 — Goudy Old Style / Cormorant Garamond /
 * Georgia serif stack on cream #FDF6EE with espresso #3B2F2F text and a
 * 1px warm-ochre border. Mirrors SourcesPanel + EraToggle styling so the
 * opening surface reads as the same period-quiet chrome family.
 *
 * Voice: Bhamwiki — factual, declarative, concrete proper nouns. Two-sentence
 * body explains what the ride is and roughly how long it takes.
 */
import { useEffect } from 'react';
import { useNavStore } from '../store/useNavStore';
import { useNavSend } from '../navigation/NavContext';
import { markBoardingReady } from '../perf/boardingReadyMark';

const SERIF_STACK =
  '"Goudy Old Style", "Cormorant Garamond", Georgia, serif';
const CREAM = '#FDF6EE';
const ESPRESSO = '#3B2F2F';
const WARM_OCHRE = '#D4A87E';

export function BoardingOverlay() {
  const navState = useNavStore((s) => s.navState);
  const send = useNavSend();

  // Plan 07-05 / PERF-02 — fire the boarding-ready performance mark as soon as
  // this overlay renders interactive content (which coincides with the Board
  // button being clickable). The helper is idempotent so a re-render won't
  // produce a second mark.
  useEffect(() => {
    if (navState === 'idle') {
      markBoardingReady();
    }
  }, [navState]);

  // Only visible in idle — click transitions idle → boarding (machine's
  // after: 1500 then auto-advances to streetcarRiding)
  if (navState !== 'idle') return null;

  const handleClick = () => {
    send({ type: 'START' });
  };

  return (
    <div
      role="dialog"
      aria-label="Board the Edgewood streetcar"
      style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 150,
        background: CREAM,
        color: ESPRESSO,
        border: `1px solid ${WARM_OCHRE}`,
        fontFamily: SERIF_STACK,
        padding: '32px 40px',
        maxWidth: 420,
        textAlign: 'center',
        boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
      }}
    >
      <h1
        style={{
          fontFamily: SERIF_STACK,
          fontWeight: 400,
          fontSize: '1.6rem',
          letterSpacing: '0.04em',
          margin: '0 0 16px',
        }}
      >
        Edgewood Lake, 1925
      </h1>
      <p
        style={{
          fontFamily: SERIF_STACK,
          fontSize: '1rem',
          lineHeight: 1.5,
          margin: '0 0 24px',
        }}
      >
        Board the No. 39 Edgewood streetcar at Five Points South. The ride
        crosses Red Mountain through the Lone Pine Gap cut and ends at the
        lake&apos;s north shore &mdash; about 90 seconds.
      </p>
      <button
        type="button"
        onClick={handleClick}
        aria-label="Board the streetcar"
        style={{
          fontFamily: SERIF_STACK,
          fontSize: '1rem',
          padding: '10px 28px',
          background: CREAM,
          color: ESPRESSO,
          border: `1px solid ${ESPRESSO}`,
          letterSpacing: '0.05em',
          cursor: 'pointer',
          minWidth: 220,
        }}
      >
        Board the streetcar
      </button>
    </div>
  );
}
